import { fmt, meta, parseKip, type CurrencyCode } from '@k2pos/core';
import { useEffect, useRef, useState } from 'react';
import { Button } from './Button';
import { Field } from './Field';
import { Mono, Text } from './Text';
import { colors } from '@/theme/tokens';
import styles from './RateSheet.module.css';

interface RateSheetProps {
  open: boolean;
  code: Exclude<CurrencyCode, 'LAK'>;
  current: number;
  onSave: (rate: number) => void;
  onClose: () => void;
}

/** Past this, a rate change is more likely a typo than a market move. */
const SANITY_FACTOR = 0.2;

/**
 * Edits the shop's exchange rate.
 *
 * Configuration, not a transaction, so it writes straight to settings rather
 * than through the outbox. The rate in force at checkout is snapshotted onto the
 * sale, so changing it here never rewrites a bill already issued.
 */
export function RateSheet({ open, code, current, onSave, onClose }: RateSheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const [draft, setDraft] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setDraft(String(current));
      setConfirmed(false);
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
  }, [open, current]);

  if (!open) return null;

  const m = meta(code);
  const next = parseKip(draft);
  const valid = next !== null && next > 0;
  // A fat-fingered 6000 for 600 makes every THB sale silently 10x wrong, and
  // nothing downstream would catch it — so a large jump asks first.
  const wild = valid && Math.abs(next - current) > current * SANITY_FACTOR;
  const canSave = valid && (!wild || confirmed);

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      onClose={onClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className={styles.sheet}>
        <div className={styles.grabber} />

        <Text weight={800} size={16}>
          ອັດຕາແລກປ່ຽນ · {m.label}
        </Text>
        <Mono size={11.5} color={colors.textMuted}>
          ດຽວນີ້ 1 {m.sym} = {fmt(current)} ₭
        </Mono>

        <div className={styles.field}>
          <Text weight={700} size={11} color={colors.textMuted}>
            1 {m.sym} ເທົ່າກັບ (₭)
          </Text>
          <Field
            value={draft}
            onChange={(v) => {
              setDraft(v);
              setConfirmed(false);
            }}
            kind="kip"
            placeholder={String(current)}
            label={`ອັດຕາແລກປ່ຽນ ${code}`}
            autoFocus
          />
        </div>

        {wild ? (
          <button
            type="button"
            onClick={() => setConfirmed((v) => !v)}
            className={styles.confirm}
          >
            <Text weight={700} size={12} color={colors.navy} align="left">
              {confirmed ? '✓ ' : ''}ປ່ຽນຫຼາຍກວ່າ 20% · ກົດເພື່ອຢືນຢັນ
            </Text>
            <Mono size={10.5} color={colors.textMuted} align="left">
              {fmt(current)} → {fmt(next)} ₭
            </Mono>
          </button>
        ) : null}

        <Text size={10.5} color={colors.textFaint}>
          ບິນທີ່ອອກໄປແລ້ວຈະບໍ່ປ່ຽນ · ເກັບອັດຕາຕອນຂາຍໄວ້ແລ້ວ
        </Text>

        <div className={styles.actions}>
          <Button label="ຍົກເລີກ" variant="outline" className="grow" onPress={onClose} />
          <Button
            label="ບັນທຶກ"
            className="grow"
            disabled={!canSave}
            onPress={() => next !== null && onSave(next)}
          />
        </div>
      </div>
    </dialog>
  );
}
